import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Images, Filter } from 'lucide-react';
import axios from 'axios';
import LikeButton from '../interactions/LikeButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const GallerySection = ({ language = 'fr' }) => {
  const [photos, setPhotos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/gallery/categories`);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch photos
  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category_id=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/gallery/photos${params}`);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Lightbox navigation
  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    setCurrentPhotoIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentPhotoIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, photos.length]);

  // Show empty state if no photos (instead of returning null which causes blank page)
  if (!loading && photos.length === 0) {
    return (
      <section className="py-12 bg-gradient-to-b from-white to-[#E8F5E9]/30" id="photogalerie">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <Images className="w-4 h-4" />
              {language === 'fr' ? 'Notre Pharmacie en Images' : 'Our Pharmacy in Pictures'}
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {language === 'fr' ? 'Photothèque' : 'Photo Gallery'}
            </h2>
          </motion.div>
          
          {/* Empty state */}
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <Images className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">
              {language === 'fr' 
                ? 'Aucune photo disponible pour le moment' 
                : 'No photos available at the moment'}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {language === 'fr' 
                ? 'Revenez bientôt pour découvrir nos dernières images !' 
                : 'Come back soon to see our latest images!'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-gradient-to-b from-white to-[#E8F5E9]/30">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Images className="w-4 h-4" />
            {language === 'fr' ? 'Notre Pharmacie en Images' : 'Our Pharmacy in Pictures'}
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {language === 'fr' ? 'Photothèque' : 'Photo Gallery'}
          </h2>
          <p className="text-gray-600 text-sm max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Découvrez notre équipe, nos événements et notre espace dédié à votre bien-être'
              : 'Discover our team, events and space dedicated to your well-being'}
          </p>
        </motion.div>

        {/* Category filters */}
        {categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-2 mb-8"
          >
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-[#2E7D32] text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-[#2E7D32] hover:text-[#2E7D32]'
              }`}
            >
              {language === 'fr' ? 'Toutes' : 'All'}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#2E7D32] text-white shadow-md'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </motion.div>
        )}

        {/* Loading state */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          /* Photo grid */
          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {photos.map((photo, index) => (
              <motion.div
                key={photo.id}
                variants={fadeInUp}
                className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={() => openLightbox(index)}
              >
                <img
                  src={photo.image_url}
                  alt={photo.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Info on hover */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <h4 className="font-semibold text-sm truncate">{photo.title}</h4>
                  <p className="text-xs text-white/80">{photo.category_name}</p>
                </div>

                {/* Featured badge */}
                {photo.is_featured && (
                  <div className="absolute top-2 left-2">
                    <span className="bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      ★ {language === 'fr' ? 'Vedette' : 'Featured'}
                    </span>
                  </div>
                )}

                {/* Like button */}
                <div 
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LikeButton
                    targetType="gallery_photo"
                    targetId={photo.id}
                    size="small"
                    showNames={false}
                    language={language}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && photos.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Navigation - Previous */}
            {photos.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}

            {/* Main image */}
            <motion.div
              key={currentPhotoIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="max-w-5xl max-h-[85vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={photos[currentPhotoIndex]?.image_url}
                alt={photos[currentPhotoIndex]?.title}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
              
              {/* Caption */}
              <div className="text-center mt-4 text-white">
                <h3 className="text-xl font-semibold">{photos[currentPhotoIndex]?.title}</h3>
                {photos[currentPhotoIndex]?.description && (
                  <p className="text-white/70 mt-1">{photos[currentPhotoIndex]?.description}</p>
                )}
                <p className="text-white/50 text-sm mt-2">
                  {photos[currentPhotoIndex]?.category_name} • {currentPhotoIndex + 1} / {photos.length}
                </p>
                {/* Like button in lightbox */}
                <div className="mt-4 flex justify-center">
                  <LikeButton
                    targetType="gallery_photo"
                    targetId={photos[currentPhotoIndex]?.id}
                    size="large"
                    showNames={true}
                    language={language}
                  />
                </div>
              </div>
            </motion.div>

            {/* Navigation - Next */}
            {photos.length > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors z-10"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            )}

            {/* Thumbnail strip */}
            {photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-xl overflow-x-auto px-4 py-2 bg-black/50 rounded-full">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={(e) => { e.stopPropagation(); setCurrentPhotoIndex(index); }}
                    className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden transition-all ${
                      index === currentPhotoIndex 
                        ? 'ring-2 ring-white scale-110' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={photo.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
